import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { LucideX, MessageCircle } from 'lucide-react';
import { useConversation } from '@/contexts/ConversationContext';
import type { Guest } from '@/types/guest.types';

interface GuestDetailsModalProps {
  open: boolean;
  onClose: () => void;
  guest: Guest | null;
  loading: boolean;
  error?: string | null;
  onEdit?: (guest: Guest) => void;
  onDelete?: (guest: Guest) => void;
}

export const GuestDetailsModal: React.FC<GuestDetailsModalProps> = ({ open, onClose, guest, loading, error}) => {
  const { t } = useTranslation(['guests', 'common']);
  const { openQuickMessage } = useConversation();
  const formatDate = (value?: Date | string | null) => {
    if (!value) return <span className="text-muted-foreground">—</span>;
    return new Date(value).toLocaleString();
  };

  const renderValue = (value?: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted-foreground">—</span>;
    }
    return value;
  };

  const getAddedByLabel = (currentGuest: Guest) => {
    if (currentGuest.creator_name) return currentGuest.creator_name;
    if (currentGuest.creator_nickname) return currentGuest.creator_nickname;
    if (currentGuest.created_by) return `User #${currentGuest.created_by}`;
    return null;
  };

  const getBelongsToLabel = (currentGuest: Guest) => {
    if (currentGuest.is_primary_guest) return null;
    if (currentGuest.primary_guest_name) return currentGuest.primary_guest_name;
    if (currentGuest.primary_guest_user_id) return `User #${currentGuest.primary_guest_user_id}`;
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <div className="relative max-w-md mx-auto">
          <button className="absolute right-4 top-4 z-10" aria-label={t('guests:details.modal.close')} onClick={onClose}>
            <LucideX className="w-5 h-5" />
          </button>
          <Card className="rounded-lg border bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-900">{t('guests:details.modal.title')}</CardTitle>
              <CardDescription className="mt-1 text-gray-600">{t('guests:details.modal.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-8 text-center text-muted-foreground">{t('common:status.loading')}</div>
              ) : error ? (
                <div className="py-8 text-center text-destructive">{error}</div>
              ) : guest ? (
                <div className="space-y-5">
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">{t('guests:fields.name')}</span>
                      <span className="text-base font-semibold text-gray-900">{guest.name || guest.nickname || `Guest #${guest.id}`}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">{t('guests:fields.rsvpStatus')}</span>
                      <span className="text-base font-semibold text-gray-900">{guest.rsvp_status}</span>
                    </div>
                  </div>

                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('guests:details.sections.contact')}</div>
                    <div className="mt-2 flex flex-col gap-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:fields.email')}</span>
                        <span className="text-sm font-semibold text-gray-900">{renderValue(guest.email)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:fields.phone')}</span>
                        <span className="text-sm font-semibold text-gray-900">{renderValue(guest.phone)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('guests:details.sections.invitation')}</div>
                    <div className="mt-2 flex flex-col gap-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.invitationSentAt')}</span>
                        <span className="text-sm font-semibold text-gray-900">{formatDate(guest.invitation_sent_at)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.invitationExpiresAt')}</span>
                        <span className="text-sm font-semibold text-gray-900">{formatDate(guest.invitation_expires_at)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.respondedAt')}</span>
                        <span className="text-sm font-semibold text-gray-900">{formatDate(guest.responded_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('guests:details.sections.guestInfo')}</div>
                    <div className="mt-2 flex flex-col gap-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.foodChoices')}</span>
                        <span className="text-sm font-semibold text-gray-900">{renderValue(guest.dietary_restrictions)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.additionalGuestType')}</span>
                        <span className="text-sm font-semibold text-gray-900">{renderValue(guest.additional_guest_type || null)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.additionalGuestCount')}</span>
                        <span className="text-sm font-semibold text-gray-900">{renderValue(guest.additional_guest_count)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.isPrimaryGuest')}</span>
                        <span className="text-sm font-semibold text-gray-900">{guest.is_primary_guest ? t('common:confirm.values.yes') : t('common:confirm.values.no')}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:fields.notes')}</span>
                        <span className="text-sm font-semibold text-gray-900">{renderValue(guest.notes)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('guests:details.sections.relationships')}</div>
                    <div className="mt-2 flex flex-col gap-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.addedBy')}</span>
                        <span className="text-sm font-semibold text-gray-900">{renderValue(getAddedByLabel(guest))}</span>
                      </div>
                      {getBelongsToLabel(guest) && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">{t('guests:details.fields.belongsTo')}</span>
                          <span className="text-sm font-semibold text-gray-900">{getBelongsToLabel(guest)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Message Button */}
                  {guest.user_id && (
                    <div className="mt-4">
                      <button
                        onClick={() => openQuickMessage({
                          recipientId: guest.user_id!,
                          recipientName: guest.name || 'Guest',
                          celebrationId: guest.celebration_id,
                        })}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {t('guests:details.actions.message', { name: guest.name || 'Guest' })}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GuestDetailsModal;
